import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { usersApi } from '@/api/users.api';
import { Field } from '@/components/ui/Field';
import { ApiError } from '@/lib/errors';
import { useToasts } from '@/context/ToastContext';

const ChangePinSchema = z
  .object({
    current_pin: z.string().regex(/^\d{6}$/, 'Current PIN must be 6 digits'),
    new_pin: z.string().regex(/^\d{6}$/, 'New PIN must be 6 digits'),
    confirm_pin: z.string().regex(/^\d{6}$/, 'Please confirm the new PIN')
  })
  .refine((data) => data.new_pin === data.confirm_pin, {
    message: 'New PIN and confirmation do not match',
    path: ['confirm_pin']
  })
  .refine((data) => data.current_pin !== data.new_pin, {
    message: 'New PIN must be different from current PIN',
    path: ['new_pin']
  });

type ChangePinValues = z.infer<typeof ChangePinSchema>;

export function ChangePinPage() {
  const { push } = useToasts();
  const [apiError, setApiError] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting }
  } = useForm<ChangePinValues>({
    resolver: zodResolver(ChangePinSchema),
    defaultValues: { current_pin: '', new_pin: '', confirm_pin: '' }
  });

  const onSubmit = handleSubmit(async (values) => {
    setApiError(null);
    try {
      await usersApi.changeOwnPin({
        current_pin: values.current_pin,
        new_pin: values.new_pin
      });
      push({ kind: 'success', title: 'PIN updated', message: 'Use the new PIN next time you sign in' });
      reset({ current_pin: '', new_pin: '', confirm_pin: '' });
    } catch (error) {
      setApiError(error instanceof ApiError ? error.problem?.detail ?? error.message : 'Update failed');
    }
  });

  return (
    <div>
      <div className="section-header">
        <h2>Change PIN</h2>
      </div>
      <div className="card" style={{ maxWidth: 520 }}>
        <div className="card-body">
          <form onSubmit={onSubmit} noValidate>
            <Field label="Current PIN" required error={errors.current_pin?.message ?? null}>
              <input
                type="password"
                inputMode="numeric"
                maxLength={6}
                className="form-input"
                autoComplete="current-password"
                {...register('current_pin')}
              />
            </Field>
            <Field label="New PIN" required error={errors.new_pin?.message ?? null}>
              <input
                type="password"
                inputMode="numeric"
                maxLength={6}
                className="form-input"
                autoComplete="new-password"
                {...register('new_pin')}
              />
            </Field>
            <Field label="Confirm new PIN" required error={errors.confirm_pin?.message ?? null}>
              <input
                type="password"
                inputMode="numeric"
                maxLength={6}
                className="form-input"
                autoComplete="new-password"
                {...register('confirm_pin')}
              />
            </Field>
            {apiError ? <div className="form-error">{apiError}</div> : null}
            <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
              {isSubmitting ? 'Updating...' : 'Update PIN'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
